"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import { FormValidationProvider, useFormValidation, validateField as validateFormField } from './formvalidator';
import { useFormSubmit } from './formsubmit';
import { usePixelatedConfig } from '../../config/config.client';
import { Loading } from '../../foundation/loading';
import { Modal } from '../../elements/modal';
import { applyWebMcpFormAttributes, applyWebMcpFieldAttributes } from '../../foundation/webmcp.utils';
import * as FC from './formcomponents';
import { CompoundFontSelector } from '../config/CompoundFontSelector';
import { FontSelector } from '../config/FontSelector';
// Merge a local components map to include config-level components without re-exporting them
export const COMPONENTS = {
    ...FC,
    CompoundFontSelector,
    FontSelector,
};
const debug = false;
/* ===== FORM ENGINE =====
Generate all the elements to display a form */
/**
 * FormEngine — Render a form defined by a JSON `formData` schema. Converts `formData.fields` to React components and manages submission handling and validation.
 *
 * Can be used in two ways:
 * 1. JSON-driven (recommended): formData.properties defines form behavior and uses the default submission lifecycle
 * 2. Code-driven (backwards compatible): Pass name, id, onSubmitHandler as props
 *
 * @param {string} [props.name] - Form HTML name attribute. Falls back to formData.properties.name
 * @param {string} [props.id] - Form HTML id attribute. Falls back to formData.properties.id
 * @param {string} [props.method] - HTTP method for form submission (default: 'post').
 * @param {function} [props.onSubmitHandler] - Optional custom submit handler invoked with the submit event.
 * @param {function} [props.onSuccess] - Optional callback invoked after a successful default submission.
 * @param {object} [props.formData] - JSON schema describing fields (object with `fields` array and optional `properties` object).
 */
FormEngine.propTypes = {
    /** Form name attribute. Falls back to formData.properties.name */
    name: PropTypes.string,
    /** Form id attribute. Falls back to formData.properties.id */
    id: PropTypes.string,
    /** Ref attached to the rendered form element */
    formRef: PropTypes.any,
    /** Callback invoked whenever a generated field changes */
    onFieldChange: PropTypes.func,
    /** HTTP method (e.g., 'post') */
    method: PropTypes.string,
    /** Custom submit handler called when the form is valid and submitted */
    onSubmitHandler: PropTypes.func,
    /** Callback invoked after a successful default submission */
    onSuccess: PropTypes.func,
    /** Controls whether query-string parameters are included in submissions */
    sendQueryParams: PropTypes.bool,
    /** JSON schema describing form fields and submission properties */
    formData: PropTypes.object.isRequired
};
export function FormEngine(props) {
    return (_jsx(FormValidationProvider, { children: _jsx(FormEngineInner, { ...props }) }));
}
/**
 * FormEngineInner — Internal implementation of the `FormEngine` that renders the generated fields and handles form submit/validation.
 *
 * @param {string} [props.name] - Form HTML name attribute.
 * @param {string} [props.id] - Form HTML id attribute.
 * @param {string} [props.method] - HTTP method for submission.
 * @param {function} [props.onSubmitHandler] - Submit handler invoked when form is validated.
 * @param {object} [props.formData] - JSON schema describing fields (object with `fields` array).
 */
FormEngineInner.propTypes = {
    /** Form name attribute */
    name: PropTypes.string,
    /** Form id attribute */
    id: PropTypes.string,
    /** Callback invoked whenever a generated field changes */
    onFieldChange: PropTypes.func,
    /** HTTP method */
    method: PropTypes.string,
    /** Custom submit handler */
    onSubmitHandler: PropTypes.func,
    /** Callback invoked after a successful default submission */
    onSuccess: PropTypes.func,
    /** Controls whether query-string parameters are included in submissions */
    sendQueryParams: PropTypes.bool,
    /** JSON form schema */
    formData: PropTypes.object.isRequired
};
function FormEngineInner(props) {
    const { validateAllFields, validateField: setFieldValidity } = useFormValidation();
    const { formRef } = props;
    const siteName = usePixelatedConfig()?.siteInfo?.name;
    const formProperties = props.formData?.properties;
    const shouldUseDefaultSubmit = Boolean(formProperties && !props.onSubmitHandler);
    const sendQueryParams = props.sendQueryParams ?? formProperties?.sendQueryParams;
    const { handleSubmit: defaultSubmitHandler, modalContent } = useFormSubmit(shouldUseDefaultSubmit ? { ...formProperties, sendQueryParams, onSuccess: props.onSuccess } : {});
    function generateFormProps(props) {
        // GENERATE PROPS TO RENDER THE FORM CONTAINER, INTERNAL FUNCTION
        if (debug)
            console.log("Generating Form Props");
        // Create a clean copy without non-serializable properties
        const { formData, onSubmitHandler, onSuccess, formRef, onFieldChange, sendQueryParams, ...formProps } = props;
        // Extract name/id from properties with fallback to props
        if (!formProps.name && formData?.properties?.name) {
            formProps.name = formData.properties.name;
        }
        if (!formProps.id && formData?.properties?.id) {
            formProps.id = formData.properties.id;
        }
        // Safety: default to POST to avoid accidental GET navigation (prevents query leakage)
        if (!formProps.method)
            formProps.method = 'post';
        applyWebMcpFormAttributes(formProps, formData, siteName);
        return formProps;
    }
    /**
     * generateNewFields — Internal: convert JSON-form `formData.fields` into React elements for rendering.
     *
     * @param {any} [props.formData] - JSON form schema with `fields` array to convert.
     */
    generateNewFields.propTypes = {
        /** JSON form schema with `fields` array */
        formData: PropTypes.any.isRequired,
        /** Callback invoked whenever a generated field changes */
        onFieldChange: PropTypes.func,
    };
    function generateNewFields(props) {
        // CORE OF THE FORM ENGINE - CONVERT JSON TO COMPONENTS - INTERNAL
        if (debug)
            console.log("Generating Form Fields");
        const newFields = [];
        const formFields = props.formData.fields;
        if (props.formData && formFields) {
            for (let field = 0; field < formFields.length; field++) {
                const thisField = formFields[field];
                // Shallow clone props to preserve function handlers (JSON stringify drops functions)
                const newProps = { ...thisField.props };
                newProps.key = `${thisField.component}-${field}`;
                applyWebMcpFieldAttributes(newProps, thisField, field, props.formData);
                if (props.onFieldChange) {
                    newProps.parent = {
                        ...(newProps.parent ?? {}),
                        onChange: props.onFieldChange,
                    };
                }
                // Convert string numeric props to numbers where needed
                const numericProps = ['maxLength', 'minLength', 'rows', 'cols', 'size', 'step'];
                numericProps.forEach(prop => {
                    if (newProps[prop] !== undefined &&
                        newProps[prop] !== null &&
                        newProps[prop] !== '') {
                        // Only convert if the value is not already a number
                        if (typeof newProps[prop] === 'string') {
                            const num = Number(newProps[prop]);
                            if (!isNaN(num)) {
                                newProps[prop] = num;
                            }
                        }
                    }
                });
                const componentName = thisField.component;
                const newElementType = COMPONENTS[componentName];
                const newElement = React.createElement(newElementType, newProps);
                newFields.push(newElement);
            }
        }
        return newFields;
    }
    async function handleSubmit(event) {
        // HANDLES THE FORM ACTION / FORM SUBMIT - EXPOSED EXTERNAL
        const form = event.currentTarget;
        const schemaFields = (props.formData?.fields ?? []).filter((field) => {
            const fieldProps = field.props ?? {};
            return fieldProps.required || fieldProps.validate || fieldProps.parent?.validate;
        });
        if (schemaFields.length > 0) {
            event.preventDefault();
            for (const field of schemaFields) {
                const fieldProps = field.props ?? {};
                const fieldElement = Array.from(form.elements).find((element) => {
                    const control = element;
                    return control.id === fieldProps.id || control.name === fieldProps.name;
                });
                if (!fieldElement)
                    continue;
                const result = await validateFormField(fieldProps, { target: fieldElement });
                fieldElement.setCustomValidity(result.isValid ? '' : result.errors.join(' '));
                const fieldId = fieldProps.id || fieldProps.name;
                if (fieldId) {
                    setFieldValidity(fieldId, result.isValid, result.errors);
                }
            }
            if (!form.reportValidity())
                return false;
        }
        else if (!form.checkValidity()) {
            return false;
        }
        // Check if form is valid before submission
        if (schemaFields.length === 0 && !validateAllFields()) {
            // Form has validation errors, don't submit
            event.preventDefault();
            return false;
        }
        const handler = shouldUseDefaultSubmit ? defaultSubmitHandler : props.onSubmitHandler;
        if (handler) {
            event.preventDefault();
            handler(event);
        }
        return true;
    }
    return (_jsxs(_Fragment, { children: [shouldUseDefaultSubmit && _jsx(Loading, {}), shouldUseDefaultSubmit && _jsx(Modal, { modalContent: modalContent }), _jsx("form", { ref: formRef, ...generateFormProps(props), onSubmit: (event) => { handleSubmit(event); }, suppressHydrationWarning: true, children: generateNewFields({
                    formData: props.formData,
                    onFieldChange: props.onFieldChange
                }) })] }));
}
